/** Minimal TopoJSON → Path2D decoder (quantized topologies), projected into world units. */
import { X, Y } from "./projection";

interface Geometry {
  type: string;
  arcs?: number[] | number[][] | number[][][];
}
export interface Topology {
  transform: { scale: [number, number]; translate: [number, number] };
  arcs: [number, number][][];
  objects: Record<string, { geometries: Geometry[] }>;
}
export type Layers = Record<string, Path2D>;

const CLOSED = new Set(["land", "lakes"]);

export function decodeTopo(topo: Topology): Layers {
  const [sx, sy] = topo.transform.scale;
  const [tx, ty] = topo.transform.translate;
  const arcs = topo.arcs.map((arc) => {
    let x = 0;
    let y = 0;
    return arc.map(([dx, dy]) => {
      x += dx;
      y += dy;
      return [X(x * sx + tx), Y(y * sy + ty)] as [number, number];
    });
  });
  const arcPts = (i: number) => (i >= 0 ? arcs[i]! : arcs[~i]!.slice().reverse());
  const lineOf = (idxs: number[]) => {
    const out: [number, number][] = [];
    idxs.forEach((i, j) => {
      const a = arcPts(i);
      for (let k = j ? 1 : 0; k < a.length; k++) out.push(a[k]!);
    });
    return out;
  };
  const toPath = (geoms: Geometry[], closed: boolean) => {
    const p = new Path2D();
    const add = (pts: [number, number][]) => {
      if (pts.length < 2) return;
      p.moveTo(pts[0]![0], pts[0]![1]);
      for (let k = 1; k < pts.length; k++) p.lineTo(pts[k]![0], pts[k]![1]);
      if (closed) p.closePath();
    };
    for (const g of geoms) {
      if (!g.arcs) continue;
      if (g.type === "Polygon") (g.arcs as number[][]).forEach((r) => add(lineOf(r)));
      else if (g.type === "MultiPolygon") (g.arcs as number[][][]).forEach((poly) => poly.forEach((r) => add(lineOf(r))));
      else if (g.type === "LineString") add(lineOf(g.arcs as number[]));
      else if (g.type === "MultiLineString") (g.arcs as number[][]).forEach((l) => add(lineOf(l)));
    }
    return p;
  };
  const out: Layers = {};
  for (const [k, obj] of Object.entries(topo.objects)) out[k] = toPath(obj.geometries, CLOSED.has(k));
  return out;
}
